import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  signal,
  untracked,
} from '@angular/core';
import {
  form as createForm,
  Field,
  required,
  schema,
} from '@angular/forms/signals';
import dayjs from 'dayjs';
import { ModalService } from '../../../../../services/modal/modal.service';
import { Checkbox } from '../../../../checkbox/checkbox.component';
import { ColorPickerComponent } from '../../../../color-picker/color-picker.component';
import { BaseConfig } from '../../../../common/config/config.adapter';
import { DatePicker } from '../../../../input/date-picker/date-picker.component';
import { InputDirective } from '../../../../input/input.directive';
import { CalendarEvent } from '../../../calendar.types';

@Component({
  selector: 'lepi-create-event-modal',
  templateUrl: './create-event.modal.html',
  standalone: true,
  imports: [Checkbox, InputDirective, ColorPickerComponent, Field, DatePicker],
})
export class CreateEventModal extends BaseConfig implements OnInit {
  readonly modalService = inject(ModalService);

  date = input<Date>();
  event = input<CalendarEvent | null>(null);

  readonly eventSchema = schema<{
    title: string;
    content: string;
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
    allDay: boolean;
    color: string;
    hasTime: boolean;
    hasEndDate: boolean;
    repeat: boolean;
  }>(schemaPath => {
    required(schemaPath.title, { message: '제목을 입력해주세요.' });
  });

  eventModel = signal<{
    title: string;
    content: string;
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
    allDay: boolean;
    color: string;
    hasTime: boolean;
    hasEndDate: boolean;
    repeat: boolean;
  }>({
    title: '',
    content: '',
    startDate: new Date(),
    endDate: new Date(),
    startTime: '',
    endTime: '',
    allDay: false,
    color: 'primary',
    hasTime: false,
    hasEndDate: false,
    repeat: false,
  });

  eventForm = createForm(this.eventModel, this.eventSchema);

  hasTime = computed(() => this.eventModel().hasTime);
  hasEndDate = computed(() => this.eventModel().hasEndDate);
  allDay = computed(() => this.eventModel().allDay);
  color = computed(() => this.eventModel().color);

  ngOnInit(): void {
    if (this.date()) {
      this.eventModel.set({
        ...this.eventModel(),
        startDate: this.date()!,
        endDate: this.date()!,
      });
    }

    if (this.event()) {
      const evt = this.event()!;
      this.eventModel.set({
        ...this.eventModel(),
        title: evt.title,
        content: evt.content || '',
        startDate: evt.startDate,
        endDate: evt.endDate,
        allDay: evt.allDay || false,
        color: evt.color || 'primary',
        hasTime: evt.hasTime || false,
        repeat: evt.repeat || false,
        hasEndDate:
          dayjs(evt.startDate).format('YYYY-MM-DD') !==
          dayjs(evt.endDate).format('YYYY-MM-DD'),
      });

      if (!evt.allDay) {
        this.eventModel.set({
          ...this.eventModel(),
          startTime: dayjs(evt.startDate).format('HH:mm'),
          endTime: dayjs(evt.endDate).format('HH:mm'),
          hasEndDate: true,
        });
      }
    }

    // Handle date swap if end is before start
    effect(() => {
      const { startDate, endDate, hasEndDate } = this.eventModel();
      if (hasEndDate && startDate && endDate) {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        if (end.isBefore(start)) {
          untracked(() => {
            this.eventModel.set({
              ...this.eventModel(),
              startDate: end.toDate(),
              endDate: start.toDate(),
            });
          });
        }
      }
    });
  }

  submit() {
    if (this.eventForm().invalid()) return;

    const values = this.eventModel();
    let { startDate, endDate } = values;

    if (this.hasTime()) {
      startDate = dayjs(startDate)
        .set(
          'hour',
          dayjs(
            `${dayjs(startDate).format('YYYY-MM-DD')} ${values.startTime}`
          ).hour()
        )
        .set(
          'minute',
          dayjs(
            `${dayjs(startDate).format('YYYY-MM-DD')} ${values.startTime}`
          ).minute()
        )
        .toDate();

      if (this.hasEndDate()) {
        endDate = dayjs(endDate)
          .set(
            'hour',
            dayjs(
              `${dayjs(endDate).format('YYYY-MM-DD')} ${values.endTime}`
            ).hour()
          )
          .set(
            'minute',
            dayjs(
              `${dayjs(endDate).format('YYYY-MM-DD')} ${values.endTime}`
            ).minute()
          )
          .toDate();
      }
    }

    if (!this.hasEndDate()) {
      endDate = startDate;
    }

    this.modalService.close({
      endDate: endDate!,
      startDate: startDate!,
      title: values.title || '',
      allDay: values.allDay || false,
      color: values.color || 'primary',
      content: values.content || '',
      hasTime: values.hasTime || false,
      repeat: values.repeat!,
    });
  }
}
